'use client';

import classnames from 'classnames';
import { useEffect, useRef, type ComponentPropsWithoutRef, type CSSProperties, type ReactNode } from 'react';
import type { PopoverPlacement } from './popover-position';
import { useAnchoredPosition } from './use-anchored-position';
import styles from './Popover.module.scss';

/** Inline style with the arrow's custom property, typed. */
type PopoverBoxStyle = CSSProperties & Record<'--popover-arrow-offset', string>;

const PLACEMENT_CLASSES: Record<PopoverPlacement, string | undefined> = {
    top: styles.top,
    bottom: styles.bottom,
    left: styles.left,
    right: styles.right,
};

interface PopoverBoxProps extends Omit<ComponentPropsWithoutRef<'div'>, 'style' | 'children'> {
    getAnchor: () => HTMLElement | null | undefined;
    placement: PopoverPlacement;
    /** Called on a pointer press outside the box and its anchor. */
    onOutsidePointerDown?: () => void;
    /** Called on Escape anywhere in the window. */
    onEscape?: () => void;
    /** Lets the pointer through the box, so one that opens under the cursor never takes its anchor's hover. */
    isPassThrough?: boolean;
    children: ReactNode;
}

/**
 * The popover box both `Popover` and the `PopoverProvider` popover draw: fixed
 * on the popover layer beside its anchor (following it while it moves), with
 * an arrow at the anchor and the outside-press and Escape listeners. Mounted
 * only while open.
 */
export function PopoverBox({
    getAnchor,
    placement,
    onOutsidePointerDown,
    onEscape,
    isPassThrough = false,
    className,
    children,
    ...boxProps
}: PopoverBoxProps): JSX.Element {
    const boxRef = useRef<HTMLDivElement>(null);
    const position = useAnchoredPosition(true, getAnchor, boxRef, placement);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent): void => {
            const target = event.target;
            if (!(target instanceof Node) || boxRef.current?.contains(target) || getAnchor()?.contains(target)) {
                return;
            }
            onOutsidePointerDown?.();
        };
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                onEscape?.();
            }
        };
        document.addEventListener('pointerdown', handlePointerDown);
        window.addEventListener('keydown', handleKeyDown);
        return (): void => {
            document.removeEventListener('pointerdown', handlePointerDown);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [getAnchor, onEscape, onOutsidePointerDown]);

    const style: PopoverBoxStyle = {
        top: `${position.top}px`,
        left: `${position.left}px`,
        pointerEvents: isPassThrough ? 'none' : undefined,
        '--popover-arrow-offset': position.arrowOffset === undefined ? '50%' : `${position.arrowOffset}px`,
    };

    return (
        <div ref={boxRef} className={classnames(styles.popover, PLACEMENT_CLASSES[position.placement], className)} style={style} {...boxProps}>
            {children}
            <div className={styles.arrow} />
        </div>
    );
}
